/**
 * Central TanStack Query key factory.
 *
 * Keys are built here, never inline at a call site, so an invalidation after a
 * mutation cannot miss a cache entry because two screens spelled the same key
 * slightly differently. Every key starts with a stable domain prefix, which
 * means `invalidateQueries({ queryKey: queryKeys.accounts.all() })` reliably
 * refreshes every account query — list, detail, and lookup alike.
 *
 * Convention: `all()` → domain root, `list(params)` → one filtered page,
 * `detail(id)` → one record. Filter objects are serialised by TanStack Query's
 * structural hashing, so key order inside the object does not matter.
 */

export interface AccountListParams {
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  search: string;
  type: string;
  status: string;
}

export interface LedgerListParams {
  page: number;
  pageSize: number;
  order: string;
  from: string;
  to: string;
  search: string;
  kind: string;
}

/** Everything that identifies one rendered financial report (FR-18). */
export interface ReportViewParams {
  type: string;
  startDate: string;
  endDate: string;
  basis: string;
  accountIds: string;
}

export interface AuditListParams {
  page: number;
  pageSize: number;
  order: string;
  action: string;
  entityType: string;
  entityId: string;
  actor: string;
  search: string;
  from: string;
  to: string;
}

export interface InvoiceListParams {
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  status: string;
  customerId: string;
  search: string;
  from: string;
  to: string;
}

/** `customers` or `vendors` — the two party directories share one factory. */
export type PartyKindKey = 'customers' | 'vendors';

export interface PartyListParams {
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  search: string;
  origin: string;
}

export interface MatchListParams {
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  status: string;
  source: string;
  minConfidence: string;
  search: string;
}

export interface EmailOutboxListParams {
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  status: string;
  category: string;
  search: string;
}

/** One filtered page of the seven-year purge review queue (FR-20). */
export interface PurgeQueueListParams {
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  entityType: string;
  decision: string;
  search: string;
}

export interface ExpenseListParams {
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  status: string;
  owner: string;
  search: string;
  from: string;
  to: string;
}

export interface NotificationListParams {
  state: string;
  type: string;
  order: string;
  search: string;
  page: number;
  pageSize: number;
}

export interface ApprovalQueueParams {
  state: string;
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  owner: string;
  search: string;
}

export interface BankTransactionListParams {
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  accountId: string;
  direction: string;
  pending: string;
  match: string;
  search: string;
  from: string;
  to: string;
}

export const queryKeys = {
  /**
   * Auth keys are owned by `lib/auth.ts` (`sessionQueryKey`,
   * `authConfigQueryKey`) because the hooks there predate this factory.
   * They are mirrored — not redefined — so a screen can invalidate the session
   * without importing the auth module.
   */
  auth: {
    session: () => ['auth', 'session'] as const,
    config: () => ['auth', 'config'] as const,
  },

  accounts: {
    all: () => ['accounts'] as const,
    list: (params: AccountListParams) => ['accounts', 'list', params] as const,
    detail: (id: string) => ['accounts', 'detail', id] as const,
    types: () => ['accounts', 'types'] as const,
    /**
     * One account's posted ledger history. Nested under `accounts` on purpose:
     * posting or reversing an entry invalidates `accounts.all()` and the
     * ledger refreshes with it.
     */
    ledger: (id: string, params: LedgerListParams) => ['accounts', 'ledger', id, params] as const,
  },

  journalEntries: {
    all: () => ['journal-entries'] as const,
    detail: (id: string) => ['journal-entries', 'detail', id] as const,
  },

  audit: {
    all: () => ['admin', 'audit'] as const,
    list: (params: AuditListParams) => ['admin', 'audit', 'list', params] as const,
    filters: () => ['admin', 'audit', 'filters'] as const,
  },

  roleMappings: {
    all: () => ['admin', 'role-mappings'] as const,
    roles: () => ['admin', 'role-mappings', 'roles'] as const,
  },

  /** Encrypted integration settings (Plaid, Stripe, AI provider, SMTP). */
  secrets: {
    all: () => ['admin', 'secrets'] as const,
    list: () => ['admin', 'secrets', 'list'] as const,
  },

  /** Expenses and their receipts (FR-4, FR-10). */
  expenses: {
    all: () => ['expenses'] as const,
    list: (params: ExpenseListParams) => ['expenses', 'list', params] as const,
    detail: (id: string) => ['expenses', 'detail', id] as const,
    /**
     * One expense's receipt-scan state. Nested under `expenses` so saving a
     * review or retrying a scan refreshes the expense with it.
     */
    ocr: (id: string) => ['expenses', 'ocr', id] as const,
    /** The employee-safe GL category vocabulary for the expense form. */
    categoryAccounts: () => ['expenses', 'category-accounts'] as const,
  },

  /**
   * The approval workbench (FR-7). Deciding on an item invalidates
   * `approvals.all()` *and* `expenses.all()` — one action changes both the
   * queue and the expense it posted.
   */
  approvals: {
    all: () => ['approvals'] as const,
    queue: (params: ApprovalQueueParams) => ['approvals', 'queue', params] as const,
    detail: (id: string) => ['approvals', 'detail', id] as const,
    settings: () => ['approvals', 'settings'] as const,
  },

  /**
   * In-app notifications (FR-7, AC-13, AC-17). `summary()` is what the header
   * bell polls every few seconds; marking anything read invalidates
   * `notifications.all()` so the badge and the centre move together.
   */
  notifications: {
    all: () => ['notifications'] as const,
    list: (params: NotificationListParams) => ['notifications', 'list', params] as const,
    summary: () => ['notifications', 'summary'] as const,
  },

  /**
   * Bank connectivity (FR-11, AC-4). `accounts()` is the Plaid links and the
   * accounts inside them; `transactions()` is the imported feed. A sync
   * touches both, so a successful sync invalidates `banking.all()`.
   */
  banking: {
    all: () => ['banking'] as const,
    accounts: () => ['banking', 'accounts'] as const,
    transactions: (params: BankTransactionListParams) =>
      ['banking', 'transactions', params] as const,
    runs: () => ['banking', 'runs'] as const,
    settings: () => ['admin', 'sync-settings'] as const,
    jobs: () => ['admin', 'jobs'] as const,
  },

  /** Receipt ↔ bank-transaction matches, their candidates, and the toggle. */
  matching: {
    all: () => ['matching'] as const,
    list: (params: MatchListParams) => ['matching', 'list', params] as const,
    detail: (id: string) => ['matching', 'detail', id] as const,
    candidates: (expenseId: string, search: string) =>
      ['matching', 'candidates', expenseId, search] as const,
    settings: () => ['matching', 'settings'] as const,
  },

  /** Outbound email: every message, its status, and its retry schedule. */
  emailOutbox: {
    all: () => ['admin', 'email-outbox'] as const,
    list: (params: EmailOutboxListParams) => ['admin', 'email-outbox', 'list', params] as const,
    summary: () => ['admin', 'email-outbox', 'summary'] as const,
  },

  /**
   * Seven-year retention review (FR-20). A decision changes both the list and
   * the counts above it, so both hang off `all()`.
   */
  purgeQueue: {
    all: () => ['admin', 'purge-queue'] as const,
    list: (params: PurgeQueueListParams) => ['admin', 'purge-queue', 'list', params] as const,
  },

  /**
   * Migrating off QuickBooks (OQ-17). `cutover()` is the boundary between
   * imported history and native records; `openingBalances()` is the wizard's
   * plan. Running an import moves both, so it invalidates `migration.all()`.
   */
  migration: {
    all: () => ['migration'] as const,
    cutover: () => ['migration', 'cutover'] as const,
    openingBalances: () => ['migration', 'opening-balances'] as const,
  },

  /**
   * Customer and vendor directories. One factory for both, keyed by kind, so
   * accepting a vendor suggestion invalidates exactly the vendor queries and
   * leaves the customer cache alone.
   */
  invoices: {
    all: () => ['invoices'] as const,
    list: (params: InvoiceListParams) => ['invoices', 'list', params] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
    settings: () => ['invoices', 'settings'] as const,
  },
  /**
   * Owner dashboard (FR-17). One entry per period, so switching the toggle
   * shows the previously fetched figures instantly while the poll refreshes.
   */
  dashboard: {
    all: () => ['dashboard'] as const,
    summary: (period: string) => ['dashboard', 'summary', period] as const,
  },

  /**
   * Standard financial reports (FR-18). `view` is keyed by the whole request —
   * type, range, basis, and account filter — because those five values are
   * exactly what the URL carries, so a deep link and a cache entry line up.
   */
  reports: {
    all: () => ['reports'] as const,
    catalogue: () => ['reports', 'catalogue'] as const,
    view: (params: ReportViewParams) => ['reports', 'view', params] as const,
  },

  /**
   * The filing entity and its partners (FR-19). One key for the whole profile:
   * completeness depends on the entity fields *and* every partner, so a partial
   * cache would let the readiness banner disagree with itself.
   */
  entitySettings: {
    all: () => ['entity-settings'] as const,
    profile: () => ['entity-settings', 'profile'] as const,
    equityAccounts: () => ['entity-settings', 'equity-accounts'] as const,
  },

  /**
   * Tax export (FR-19 / AC-7). `export(jobId)` is polled while a job runs;
   * `readiness(year)` is keyed by year because a template can exist for one
   * year and not another.
   */
  tax: {
    all: () => ['tax'] as const,
    availableYears: () => ['tax', 'available-years'] as const,
    readiness: (taxYear: number) => ['tax', 'readiness', taxYear] as const,
    exports: () => ['tax', 'exports'] as const,
    export: (jobId: string) => ['tax', 'exports', jobId] as const,
  },

  parties: {
    all: (kind: PartyKindKey) => [kind] as const,
    list: (kind: PartyKindKey, params: PartyListParams) => [kind, 'list', params] as const,
    detail: (kind: PartyKindKey, id: string) => [kind, 'detail', id] as const,
    categories: (kind: PartyKindKey) => [kind, 'categories'] as const,
    suggestions: (kind: PartyKindKey) => [kind, 'suggestions'] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;
